import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Popconfirm, Row, Select, Space, Switch, Table, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import SuperAdminContext from "../../context/SuperAdminContext";
import { ClientProps } from "../../interfaces/interfaces";
import { CardComponent, ColumnComponent } from "../../components/ui";

export const SuperAdminSubscriptionsPage = () => {
  const {
    companies,
    superAdminLoading,
    getCompanies,
    updateCompanySubscription,
  } = useContext(SuperAdminContext);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string | undefined>();

  useEffect(() => {
    getCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(
    () =>
      companies.filter((company) => {
        const matchesSearch = company.name
          .toLowerCase()
          .includes(search.toLowerCase());
        const matchesStatus =
          !status ||
          (status === "active" && company.active) ||
          (status === "suspended" && !company.active);
        return matchesSearch && matchesStatus;
      }),
    [companies, search, status],
  );

  const columns: ColumnsType<ClientProps> = [
    { title: "Company", dataIndex: "name", key: "name" },
    {
      title: "Email / Contact",
      key: "contact",
      render: (_, company) => (
        <Space direction="vertical" size={0}>
          <span>{company.email || ""}</span>
          <span className="super-admin__muted">{company.contact || ""}</span>
        </Space>
      ),
    },
    {
      title: "QuickBooks",
      dataIndex: "quickbooksStatus",
      key: "quickbooksStatus",
      render: (quickbooksStatus) => (
        <Tag color={quickbooksStatus?.connected ? "green" : "default"}>
          {quickbooksStatus?.connected ? "Connected" : "Not connected"}
        </Tag>
      ),
    },
    {
      title: "Subscription",
      dataIndex: "active",
      key: "active",
      render: (active) => (
        <Tag color={active ? "green" : "orange"}>
          {active ? "Active" : "Suspended"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, company) => (
        <Popconfirm
          title={
            company.active
              ? "Suspend this company subscription?"
              : "Activate this company subscription?"
          }
          onConfirm={() =>
            updateCompanySubscription(company._id, !company.active, getCompanies)
          }
        >
          <Switch
            checked={company.active}
            checkedChildren="Active"
            unCheckedChildren="Suspended"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="super-admin">
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardComponent
            className="summary-client__card"
            title="Subscriptions"
            extra={
              <Space className="super-admin__filters" wrap>
                <Input.Search
                  allowClear
                  placeholder="Search companies"
                  onChange={(event) => setSearch(event.target.value)}
                  style={{ minWidth: 220 }}
                />
                <Select
                  allowClear
                  placeholder="Subscription status"
                  value={status}
                  onChange={setStatus}
                  style={{ minWidth: 180 }}
                  options={[
                    { label: "Active", value: "active" },
                    { label: "Suspended", value: "suspended" },
                  ]}
                />
                <Button icon={<ReloadOutlined />} onClick={getCompanies}>
                  Refresh
                </Button>
              </Space>
            }
          />
        </ColumnComponent>
        <ColumnComponent>
          <Table
            bordered
            size="small"
            rowKey="_id"
            columns={columns}
            dataSource={rows}
            loading={superAdminLoading}
            scroll={{ y: "70vh", x: "100%" }}
          />
        </ColumnComponent>
      </Row>
    </div>
  );
};
